import type { AssociationRow } from './analyticsTypes';
import { average, getActivePmidsCurrentYear, getAssociatedCount } from './analyticsHelpers';

export type AnalyticsTabKey = 'GDA' | 'VDA' | 'DDA';

export interface AnalyticsSummaryCard {
  id: string;
  label: string | ((currentYear: number) => string);
  getValue: (data: AssociationRow[], config: AnalyticsConfig) => number;
  decimals?: number;
}

export interface AnalyticsScatterTab {
  value: string;
  label: string;
  metricKey: string;
  axisLabel: string;
  xMin?: number;
  xMax?: number;
}

export interface AnalyticsConfig {
  tab: AnalyticsTabKey;
  analyticsTitle: string;
  entityLabel: string;
  topBarTitle: string;
  topScatterTitle: string;
  fields: {
    entityId: string;
    associatedCount: string;
    avgPerEntity: string;
    score: string;
    papers: string;
    pmids: string;
    ei: string;
    yearFinal?: string;
    ct?: string;
  };
  labels: {
    associatedCount: string;
    avgPerEntity: string;
    scoreAxis: string;
    papersAxis: string;
    scatterY: string;
    entityAxis: string;
    distributionCountNoun: string;
  };
  orderBy: string;
  filterScoreField: string;
  summaryCards: AnalyticsSummaryCard[];
  scatterTabs: AnalyticsScatterTab[];
  features: {
    ctLegend: boolean;
    publicationTrend: boolean;
    metricScatter: boolean;
  };
}

const GDA_CONFIG: AnalyticsConfig = {
  tab: 'GDA',
  analyticsTitle: 'GDA analytics',
  entityLabel: 'Gene',
  topBarTitle: 'Top 15 genes by normalized score',
  topScatterTitle: 'Top 50 genes by normalized score',
  fields: {
    entityId: 'geneSymbol',
    associatedCount: 'numGenesAssociatedToDisease',
    avgPerEntity: 'numDiseasesAssociatedToGene',
    score: 'normalized_score',
    papers: 'numPMIDsupportingAssociation',
    pmids: 'numPMIDsupportingAssociation',
    ei: 'EI',
    yearFinal: 'yearFinal',
    ct: 'numCTs',
  },
  labels: {
    associatedCount: 'Associated genes',
    avgPerEntity: 'Average diseases per gene',
    scoreAxis: 'Normalized score',
    papersAxis: 'Publications (PMIDs)',
    scatterY: 'Normalized score',
    entityAxis: 'Gene',
    distributionCountNoun: 'gene(s)',
  },
  orderBy: 'normalized_score',
  filterScoreField: 'normalized_score',
  summaryCards: [
    {
      id: 'associated',
      label: 'Associated genes',
      getValue: (data, config) => getAssociatedCount(data, config.fields.associatedCount),
    },
    {
      id: 'avg-ei',
      label: 'Average EI (evidence index)',
      getValue: (data, config) =>
        average(data.map(row => Number(row[config.fields.ei])).filter(Number.isFinite)),
      decimals: 2,
    },
    {
      id: 'avg-per-entity',
      label: 'Average diseases per gene',
      getValue: (data, config) =>
        average(data.map(row => Number(row[config.fields.avgPerEntity])).filter(Number.isFinite)),
    },
    {
      id: 'active-pmids',
      label: year => `Active PMIDs in ${year}`,
      getValue: (data, config) =>
        getActivePmidsCurrentYear(data, config.fields.pmids, config.fields.yearFinal),
    },
  ],
  scatterTabs: [
    {
      value: 'geneDSI',
      label: 'DSI vs score',
      metricKey: 'geneDSI',
      axisLabel: 'DSI',
      xMin: 0.2,
      xMax: 0.8,
    },
    {
      value: 'geneDPI',
      label: 'DPI vs score',
      metricKey: 'geneDPI',
      axisLabel: 'DPI',
      xMin: 0,
      xMax: 1,
    },
  ],
  features: { ctLegend: true, publicationTrend: true, metricScatter: true },
};

const VDA_CONFIG: AnalyticsConfig = {
  tab: 'VDA',
  analyticsTitle: 'VDA analytics',
  entityLabel: 'Variant',
  topBarTitle: 'Top 15 variants by normalized score',
  topScatterTitle: 'Top 50 variants by normalized score',
  fields: {
    entityId: 'variantStrID',
    associatedCount: 'numVariantsAssociatedToDisease',
    avgPerEntity: 'numDiseasesAssociatedToVariant',
    score: 'normalized_score',
    papers: 'numPMIDsupportingAssociation',
    pmids: 'numPMIDsupportingAssociation',
    ei: 'EI',
    yearFinal: 'yearFinal',
  },
  labels: {
    associatedCount: 'Associated variants',
    avgPerEntity: 'Average diseases per variant',
    scoreAxis: 'Normalized score',
    papersAxis: 'Publications (PMIDs)',
    scatterY: 'Normalized score',
    entityAxis: 'Variant',
    distributionCountNoun: 'variant(s)',
  },
  orderBy: 'normalized_score',
  filterScoreField: 'normalized_score',
  summaryCards: [
    {
      id: 'associated',
      label: 'Associated variants',
      getValue: (data, config) => getAssociatedCount(data, config.fields.associatedCount),
    },
    {
      id: 'avg-ei',
      label: 'Average EI (evidence index)',
      getValue: (data, config) =>
        average(data.map(row => Number(row[config.fields.ei])).filter(Number.isFinite)),
      decimals: 2,
    },
    {
      id: 'avg-per-entity',
      label: 'Average diseases per variant',
      getValue: (data, config) =>
        average(data.map(row => Number(row[config.fields.avgPerEntity])).filter(Number.isFinite)),
    },
    {
      id: 'active-pmids',
      label: year => `Active PMIDs in ${year}`,
      getValue: (data, config) =>
        getActivePmidsCurrentYear(data, config.fields.pmids, config.fields.yearFinal),
    },
  ],
  scatterTabs: [
    {
      value: 'variantDSI',
      label: 'DSI vs score',
      metricKey: 'variantDSI',
      axisLabel: 'DSI',
      xMin: 0.2,
      xMax: 0.8,
    },
    {
      value: 'variantDPI',
      label: 'DPI vs score',
      metricKey: 'variantDPI',
      axisLabel: 'DPI',
      xMin: 0,
      xMax: 1,
    },
  ],
  features: { ctLegend: false, publicationTrend: true, metricScatter: true },
};

const DDA_CONFIG: AnalyticsConfig = {
  tab: 'DDA',
  analyticsTitle: 'DDA analytics',
  entityLabel: 'Disease pair',
  topBarTitle: 'Top 15 disease pairs by Jaccard index (genes)',
  topScatterTitle: 'Top 50 disease pairs by Jaccard index (genes)',
  fields: {
    entityId: 'disease2_Name',
    associatedCount: 'shared_genes',
    avgPerEntity: 'shared_variants',
    score: 'jaccard_genes',
    papers: 'shared_genes',
    pmids: 'shared_genes',
    ei: 'jaccard_variants',
    yearFinal: undefined,
  },
  labels: {
    associatedCount: 'Disease pairs',
    avgPerEntity: 'Average shared variants',
    scoreAxis: 'Jaccard index (genes)',
    papersAxis: 'Shared genes',
    scatterY: 'Jaccard index (genes)',
    entityAxis: 'Disease',
    distributionCountNoun: 'pair(s)',
  },
  orderBy: 'jaccard_genes',
  filterScoreField: 'jaccard_genes',
  summaryCards: [
    {
      id: 'pairs',
      label: 'Disease pairs',
      getValue: data => data.length,
    },
    {
      id: 'avg-ji-genes',
      label: 'Average JI (genes)',
      getValue: (data, config) =>
        average(data.map(row => Number(row[config.fields.score])).filter(Number.isFinite)),
      decimals: 3,
    },
    {
      id: 'avg-shared-genes',
      label: 'Average shared genes',
      getValue: (data, config) => average(data.map(row => Number(row.shared_genes)).filter(Number.isFinite)),
    },
    {
      id: 'avg-shared-variants',
      label: 'Average shared variants',
      getValue: (data, config) =>
        average(data.map(row => Number(row.shared_variants)).filter(Number.isFinite)),
    },
  ],
  scatterTabs: [
    {
      value: 'jaccard_variants',
      label: 'JI variants vs JI genes',
      metricKey: 'jaccard_variants',
      axisLabel: 'Jaccard index (variants)',
      xMin: 0,
      xMax: 1,
    },
    {
      value: 'shared_variants',
      label: 'Shared variants vs JI genes',
      metricKey: 'shared_variants',
      axisLabel: 'Shared variants',
    },
  ],
  features: { ctLegend: false, publicationTrend: false, metricScatter: true },
};

export const ANALYTICS_CONFIGS: Record<AnalyticsTabKey, AnalyticsConfig> = {
  GDA: GDA_CONFIG,
  VDA: VDA_CONFIG,
  DDA: DDA_CONFIG,
};

type AnalyticsViewPerspective = 'byDisease' | 'byGene' | 'byVariant' | 'byDiseasePair';

const ANCHOR_ENTITY_SCATTER_TABS: AnalyticsScatterTab[] = [
  {
    value: 'ei',
    label: 'EI vs score',
    metricKey: 'EI',
    axisLabel: 'Evidence index',
    xMin: 0,
    xMax: 1,
  },
  {
    value: 'publications',
    label: 'Publications vs score',
    metricKey: 'numPMIDsupportingAssociation',
    axisLabel: 'Publications (PMIDs)',
  },
];

function buildAnchorEntitySummaryCards(
  associatedLabel: string,
  associatedField: string,
  avgPublicationsLabel: string,
): AnalyticsSummaryCard[] {
  return [
    {
      id: 'associated',
      label: associatedLabel,
      getValue: (data, config) => getAssociatedCount(data, associatedField || config.fields.associatedCount),
    },
    {
      id: 'avg-ei',
      label: 'Average EI (evidence index)',
      getValue: (data, config) =>
        average(data.map(row => Number(row[config.fields.ei])).filter(Number.isFinite)),
      decimals: 2,
    },
    {
      id: 'avg-publications',
      label: avgPublicationsLabel,
      getValue: (data, config) =>
        average(data.map(row => Number(row[config.fields.papers])).filter(Number.isFinite)),
    },
    {
      id: 'active-pmids',
      label: year => `Active PMIDs in ${year}`,
      getValue: (data, config) =>
        getActivePmidsCurrentYear(data, config.fields.pmids, config.fields.yearFinal),
    },
  ];
}

function getViewPerspective(tab: AnalyticsTabKey, view: string): AnalyticsViewPerspective {
  if (tab === 'DDA') return 'byDiseasePair';
  if (tab === 'GDA' && view === 'GENES') return 'byGene';
  if (tab === 'GDA' && view === 'VARIANTS') return 'byGene';
  if (tab === 'VDA' && view === 'VARIANTS') return 'byVariant';
  if (tab === 'GDA') return 'byDisease';
  if (tab === 'VDA') return 'byDisease';
  return 'byDisease';
}

function applyViewPerspective(base: AnalyticsConfig, perspective: AnalyticsViewPerspective): AnalyticsConfig {
  if (perspective === 'byDiseasePair') return base;

  if (perspective === 'byGene') {
    return {
      ...base,
      entityLabel: 'Disease',
      topBarTitle: 'Top 15 diseases by normalized score',
      topScatterTitle: 'Top 50 diseases by normalized score',
      fields: {
        ...base.fields,
        entityId: 'diseaseName',
        associatedCount: base.tab === 'GDA' ? 'num_diseases' : 'numDiseasesAssociatedToVariant',
      },
      labels: {
        ...base.labels,
        associatedCount: 'Associated diseases',
        avgPerEntity: 'Average publications per disease',
        entityAxis: 'Disease',
        distributionCountNoun: 'disease(s)',
      },
      summaryCards: buildAnchorEntitySummaryCards(
        'Associated diseases',
        base.tab === 'GDA' ? 'num_diseases' : 'numDiseasesAssociatedToVariant',
        'Average publications per disease',
      ),
      scatterTabs: ANCHOR_ENTITY_SCATTER_TABS,
      features: { ...base.features, metricScatter: true },
    };
  }

  if (perspective === 'byVariant') {
    return {
      ...base,
      entityLabel: 'Disease',
      topBarTitle: 'Top 15 diseases by normalized score',
      topScatterTitle: 'Top 50 diseases by normalized score',
      fields: {
        ...base.fields,
        entityId: 'diseaseName',
        associatedCount: 'numDiseasesAssociatedToVariant',
      },
      labels: {
        ...base.labels,
        associatedCount: 'Associated diseases',
        avgPerEntity: 'Average publications per disease',
        entityAxis: 'Disease',
        distributionCountNoun: 'disease(s)',
      },
      summaryCards: buildAnchorEntitySummaryCards(
        'Associated diseases',
        'numDiseasesAssociatedToVariant',
        'Average publications per disease',
      ),
      scatterTabs: ANCHOR_ENTITY_SCATTER_TABS,
      features: { ...base.features, ctLegend: false, metricScatter: true },
    };
  }

  return base;
}

const RESOLVED_CONFIG_CACHE = new Map<string, AnalyticsConfig>();

export function getAnalyticsConfig(tab: string, view = 'DISEASES'): AnalyticsConfig | null {
  if (tab !== 'GDA' && tab !== 'VDA' && tab !== 'DDA') {
    return null;
  }

  const cacheKey = `${tab}:${view}`;
  const cached = RESOLVED_CONFIG_CACHE.get(cacheKey);
  if (cached) return cached;

  const base = ANALYTICS_CONFIGS[tab];
  const perspective = getViewPerspective(tab, view);
  const resolved = applyViewPerspective(base, perspective);
  RESOLVED_CONFIG_CACHE.set(cacheKey, resolved);
  return resolved;
}
